import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import Toast from '../components/Toast';

const EmailVerificationPage = () => {
    const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'already_verified'
    const [message, setMessage] = useState('');
    const [toast, setToast] = useState(null);

    useEffect(() => {
        const verifyEmail = async () => {
            try {
                // URL'den hash parametrelerini al
                const hashParams = new URLSearchParams(window.location.hash.substring(1));
                const accessToken = hashParams.get('access_token');
                const type = hashParams.get('type');

                if (!accessToken) {
                    setStatus('error');
                    setMessage('Doğrulama linki geçersiz veya eksik. Lütfen e-postanızdaki linki kontrol edin.');
                    return;
                }

                // Email verification için type kontrolü
                if (type === 'recovery') {
                    // Password recovery - bu sayfaya yönlendirilmeli değil
                    setStatus('error');
                    setMessage('Bu sayfa e-posta doğrulama için. Şifre sıfırlama için doğru sayfaya yönlendirileceksiniz.');
                    return;
                }

                // Supabase otomatik olarak hash'ten session oluşturur
                // getSession ile mevcut session'ı kontrol et
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();

                if (sessionError) {
                    setStatus('error');
                    setMessage('Doğrulama hatası: ' + sessionError.message);
                    return;
                }

                if (session && session.user) {
                    // Session başarıyla oluşturuldu, email doğrulaması tamamlandı
                    if (session.user.email_confirmed_at) {
                        setStatus('success');
                        setMessage('E-posta adresiniz başarıyla doğrulandı! Yönlendiriliyorsunuz...');
                        
                        // URL'yi temizle
                        window.history.replaceState(null, '', '/verify-email');
                        
                        // 2 saniye sonra dashboard'a yönlendir
                        setTimeout(() => {
                            window.location.href = '/';
                        }, 2000);
                    } else {
                        // Session var ama email henüz confirmed değil (nadir durum)
                        // Yine de başarılı sayılabilir çünkü session oluştu
                        setStatus('success');
                        setMessage('E-posta doğrulaması tamamlandı! Yönlendiriliyorsunuz...');
                        
                        window.history.replaceState(null, '', '/verify-email');
                        
                        setTimeout(() => {
                            window.location.href = '/';
                        }, 2000);
                    }
                } else {
                    // Session yoksa, getUser ile kontrol et
                    const { data: { user }, error: userError } = await supabase.auth.getUser();

                    if (userError) {
                        setStatus('error');
                        setMessage('Oturum açılamadı. Lütfen doğrulama linkini tekrar kontrol edin veya giriş yapmayı deneyin.');
                        return;
                    }

                    if (user && user.email_confirmed_at) {
                        setStatus('success');
                        setMessage('E-posta adresiniz zaten doğrulanmış! Yönlendiriliyorsunuz...');
                        
                        setTimeout(() => {
                            window.location.href = '/';
                        }, 2000);
                    } else {
                        setStatus('error');
                        setMessage('E-posta doğrulaması tamamlanamadı. Lütfen linki tekrar kontrol edin.');
                    }
                }
            } catch (error) {
                console.error('Email verification error:', error);
                setStatus('error');
                setMessage('Bir hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
            }
        };

        verifyEmail();
    }, []);

    const renderContent = () => {
        switch (status) {
            case 'verifying':
                return (
                    <div className="text-center">
                        <Loader2 className="h-16 w-16 text-purple-600 animate-spin mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            E-posta Doğrulanıyor
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400">
                            Lütfen bekleyin, e-posta adresiniz doğrulanıyor...
                        </p>
                    </div>
                );

            case 'success':
                return (
                    <div className="text-center">
                        <div className="bg-green-100 dark:bg-green-900/20 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                            <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            E-posta Doğrulandı!
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {message || 'E-posta adresiniz başarıyla doğrulandı. Ana sayfaya yönlendiriliyorsunuz...'}
                        </p>
                        <div className="flex items-center justify-center space-x-2 text-purple-600 dark:text-purple-400">
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <span className="text-sm">Yönlendiriliyor...</span>
                        </div>
                    </div>
                );

            case 'already_verified':
                return (
                    <div className="text-center">
                        <div className="bg-blue-100 dark:bg-blue-900/20 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                            <Mail className="h-12 w-12 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            Zaten Doğrulanmış
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Bu e-posta adresi zaten doğrulanmış. Ana sayfaya yönlendiriliyorsunuz...
                        </p>
                        <button
                            onClick={() => window.location.href = '/'}
                            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                        >
                            Ana Sayfaya Git
                        </button>
                    </div>
                );

            case 'error':
            default:
                return (
                    <div className="text-center">
                        <div className="bg-red-100 dark:bg-red-900/20 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                            <XCircle className="h-12 w-12 text-red-600 dark:text-red-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            Doğrulama Hatası
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {message || 'E-posta doğrulaması tamamlanamadı. Lütfen linki kontrol edin veya tekrar deneyin.'}
                        </p>
                        <div className="space-y-3">
                            <button
                                onClick={() => window.location.href = '/auth'}
                                className="block w-full px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                            >
                                Giriş Sayfasına Git
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                className="block w-full px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                            >
                                Tekrar Dene
                            </button>
                        </div>
                    </div>
                );
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
                {renderContent()}
            </div>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default EmailVerificationPage;
